import { supabase } from '@/src/core/config/supabase';
import { useTranslation } from '@/src/i18n';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';
import { useRef, useState } from 'react';
import { Platform } from 'react-native';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';
import { moderateContentUseCase } from '@/src/core/dependencies';
import { ModerationError, ModerationFieldType } from '@/src/domain/models/Moderation';

// Image size presets for different use cases
const IMAGE_PRESETS = {
  avatar: {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.7,
    aspect: [1, 1] as [number, number],
  },
  post: {
    maxWidth: 1200,
    maxHeight: 1200,
    quality: 0.75,
    aspect: undefined, // Free aspect for posts
  },
  default: {
    maxWidth: 1000,
    maxHeight: 1000,
    quality: 0.8,
    aspect: [1, 1] as [number, number],
  },
};

type ImagePreset = keyof typeof IMAGE_PRESETS;

interface UseImageUploadOptions {
  bucket: string;
  userId: string;
  preset?: ImagePreset;
  fieldType?: ModerationFieldType;
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

// Helper to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const useImageUpload = ({
  bucket,
  userId,
  preset = 'default',
  fieldType,
  onSuccess,
  onError,
}: UseImageUploadOptions) => {
  const [uploading, setUploading] = useState(false);
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const config = IMAGE_PRESETS[preset];

  // Web-specific: create and trigger file input
  const pickImageWeb = (): Promise<{ uri: string; width: number; height: number } | null> => {
    return new Promise((resolve) => {
      // Create file input if it doesn't exist
      if (!fileInputRef.current) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.style.display = 'none';
        document.body.appendChild(input);
        fileInputRef.current = input;
      }

      const input = fileInputRef.current;

      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) {
          resolve(null);
          return;
        }

        // Create object URL and get dimensions
        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
          resolve({
            uri: url,
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };

        img.src = url;
      };

      // Reset and trigger
      input.value = '';
      input.click();
    });
  };

  // Web-specific: resize image using Canvas
  const optimizeImageWeb = async (
    uri: string,
    originalWidth: number,
    originalHeight: number
  ): Promise<Blob> => {
    const { maxWidth, maxHeight, quality } = config;

    // Calculate new dimensions while maintaining aspect ratio
    let newWidth = originalWidth;
    let newHeight = originalHeight;

    if (originalWidth > maxWidth || originalHeight > maxHeight) {
      const widthRatio = maxWidth / originalWidth;
      const heightRatio = maxHeight / originalHeight;
      const ratio = Math.min(widthRatio, heightRatio);

      newWidth = Math.round(originalWidth * ratio);
      newHeight = Math.round(originalHeight * ratio);
    }

    logger.debug('ImageUpload', `Resizing: ${originalWidth}x${originalHeight} -> ${newWidth}x${newHeight}`);

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Draw image with smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = uri;
    });
  };

  // Mobile-specific: optimize image using ImageManipulator
  const optimizeImageMobile = async (
    uri: string,
    originalWidth: number,
    originalHeight: number
  ): Promise<string> => {
    const { maxWidth, maxHeight, quality } = config;

    // Calculate new dimensions while maintaining aspect ratio
    let newWidth = originalWidth;
    let newHeight = originalHeight;

    if (originalWidth > maxWidth || originalHeight > maxHeight) {
      const widthRatio = maxWidth / originalWidth;
      const heightRatio = maxHeight / originalHeight;
      const ratio = Math.min(widthRatio, heightRatio);

      newWidth = Math.round(originalWidth * ratio);
      newHeight = Math.round(originalHeight * ratio);
    }

    logger.debug('ImageUpload', `Resizing: ${originalWidth}x${originalHeight} -> ${newWidth}x${newHeight}`);

    // Resize and compress the image
    const manipulatedImage = await ImageManipulator.manipulateAsync(
      uri,
      [{ resize: { width: newWidth, height: newHeight } }],
      {
        compress: quality,
        format: ImageManipulator.SaveFormat.JPEG,
      }
    );

    return manipulatedImage.uri;
  };

  const pickAndUploadImage = async () => {
    try {
      if (Platform.OS === 'web') {
        // Web: use file input
        const result = await pickImageWeb();
        if (result) {
          await uploadImageWeb(result.uri, result.width, result.height);
        }
      } else {
        // Mobile: use ImagePicker
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();

        if (status !== 'granted') {
          platformAlert(t('permissions.permissionRequired'), t('permissions.photoPermission'));
          return;
        }

        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ['images'],
          allowsEditing: true,
          aspect: config.aspect,
          quality: 1, // Get full quality, we'll compress later with more control
        });

        if (!result.canceled && result.assets[0]) {
          await uploadImageMobile(result.assets[0].uri, result.assets[0].width, result.assets[0].height);
        }
      }
    } catch (error) {
      logger.error('ImageUpload', 'Failed to pick image', { error });
      platformAlert(t('common.error'), t('permissions.failedToPick'));
      onError?.(error as Error);
    }
  };

  // Web upload flow
  const uploadImageWeb = async (uri: string, width: number, height: number) => {
    try {
      setUploading(true);

      // Optimize the image using Canvas
      const optimizedBlob = await optimizeImageWeb(uri, width, height);

      logger.debug('ImageUpload', `Optimized: ${formatFileSize(optimizedBlob.size)}`);

      // Convert blob to ArrayBuffer
      const arrayBuffer = await optimizedBlob.arrayBuffer();

      const fileName = `${userId}-${Date.now()}.jpg`;
      const filePath = `${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, arrayBuffer, {
          contentType: 'image/jpeg',
          upsert: true,
        });

      if (uploadError) {
        logger.error('ImageUpload', 'Failed to upload image', { error: uploadError });
        platformAlert(t('common.error'), `${t('permissions.failedToUpload')}: ${uploadError.message}`);
        onError?.(uploadError);
        return;
      }

      logger.info('ImageUpload', 'Image uploaded successfully');

      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(filePath);

      if (urlData?.publicUrl) {
        const urlWithTimestamp = `${urlData.publicUrl}?t=${Date.now()}`;

        // Moderate image before returning URL
        if (fieldType) {
          try {
            await moderateContentUseCase.executeImage(urlWithTimestamp, fieldType, userId);
          } catch (moderationError) {
            if (moderationError instanceof ModerationError) {
              logger.warn('ImageUpload', 'Image blocked by moderation', { reasons: (moderationError as ModerationError).reasons });
              await supabase.storage.from(bucket).remove([filePath]);
              platformAlert(t('common.error'), t('moderation.imageBlocked'));
              return;
            }
            logger.error('ImageUpload', 'Moderation call failed (non-moderation error)', { error: moderationError });
            throw moderationError;
          }
        }

        onSuccess?.(urlWithTimestamp);
        platformAlert(t('common.success'), t('permissions.photoUploadSuccess'));

        // Clean up object URL
        URL.revokeObjectURL(uri);

        return urlWithTimestamp;
      }
    } catch (error) {
      logger.error('ImageUpload', 'Failed to upload image (outer catch)', { error });
      platformAlert(t('common.error'), `${t('permissions.failedToUpload')}: ${error}`);
      onError?.(error as Error);
    } finally {
      setUploading(false);
    }
  };

  // Mobile upload flow
  const uploadImageMobile = async (uri: string, width?: number, height?: number) => {
    try {
      setUploading(true);

      // Get original file size
      const originalResponse = await fetch(uri);
      const originalBlob = await originalResponse.blob();
      const originalSize = originalBlob.size;

      logger.debug('ImageUpload', `Original image: ${formatFileSize(originalSize)}`);

      // Optimize the image if we have dimensions
      let optimizedUri = uri;
      if (width && height) {
        optimizedUri = await optimizeImageMobile(uri, width, height);
      }

      // Use fetch to read the optimized file as a blob
      const response = await fetch(optimizedUri);
      const blob = await response.blob();

      // Convert blob to ArrayBuffer using FileReader (React Native compatible)
      const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result instanceof ArrayBuffer) {
            resolve(reader.result);
          } else {
            reject(new Error('Failed to read file as ArrayBuffer'));
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(blob);
      });

      const optimizedSize = arrayBuffer.byteLength;
      const savings = ((originalSize - optimizedSize) / originalSize) * 100;

      logger.debug('ImageUpload', `Optimized: ${formatFileSize(optimizedSize)} (${savings.toFixed(0)}% smaller)`);

      const fileName = `${userId}-${Date.now()}.jpg`;
      const filePath = `${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, arrayBuffer, {
          contentType: 'image/jpeg',
          upsert: true,
        });

      if (uploadError) {
        logger.error('ImageUpload', 'Failed to upload image', { error: uploadError });
        platformAlert(t('common.error'), `${t('permissions.failedToUpload')}: ${uploadError.message}`);
        onError?.(uploadError);
        return;
      }

      logger.info('ImageUpload', 'Image uploaded successfully');

      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(filePath);

      if (urlData?.publicUrl) {
        const urlWithTimestamp = `${urlData.publicUrl}?t=${Date.now()}`;

        // Moderate image before returning URL
        if (fieldType) {
          try {
            await moderateContentUseCase.executeImage(urlWithTimestamp, fieldType, userId);
          } catch (moderationError) {
            if (moderationError instanceof ModerationError) {
              logger.warn('ImageUpload', 'Image blocked by moderation', { reasons: (moderationError as ModerationError).reasons });
              await supabase.storage.from(bucket).remove([filePath]);
              platformAlert(t('common.error'), t('moderation.imageBlocked'));
              return;
            }
            logger.error('ImageUpload', 'Moderation call failed (non-moderation error)', { error: moderationError });
            throw moderationError;
          }
        }

        onSuccess?.(urlWithTimestamp);
        platformAlert(t('common.success'), t('permissions.photoUploadSuccess'));
        return urlWithTimestamp;
      }
    } catch (error) {
      logger.error('ImageUpload', 'Failed to upload image (outer catch)', { error });
      platformAlert(t('common.error'), `${t('permissions.failedToUpload')}: ${error}`);
      onError?.(error as Error);
    } finally {
      setUploading(false);
    }
  };

  return {
    pickAndUploadImage,
    uploading,
  };
};
